import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { C_ON_SURFACE_LOW_EMPHASIS, C_PRIMARY, C_SURFACE } from "./icon/Colors";
import { ELEVATION_2 } from "./icon/Elevation";
import { DateFormatType, getPattern } from "../utils/DateFormatter";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import type { ReactNode } from "react";
import { InputAdornment, TextField } from "../mui";
import ErrorIcon from "@mui/icons-material/Error";

export interface MUIDateInputProps {
  date: Date | null;
  onChange?: (date: Date) => void;
  label?: string;
  helperLabel?: React.ReactNode;
  disabled?: boolean;
  isInvalid?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isDayDisabled?: (date: Date) => boolean;
  dataTestId?: string;
  startAdornment?: ReactNode;
  isHighlight?: boolean;
  size?: "small" | "medium";
  showClear?: boolean;
  onClear?: () => void;
  showErrorIcon?: boolean;
  onBlur?: (newDate?: Date) => void;
  onFocus?: () => void;
}

export const MUIDateInput = ({
  date,
  onChange,
  label,
  helperLabel,
  disabled,
  isInvalid,
  minDate,
  maxDate,
  isDayDisabled,
  dataTestId,
  startAdornment,
  isHighlight,
  size,
  showClear,
  onClear,
  showErrorIcon,
  onBlur,
  onFocus,
}: MUIDateInputProps) => {
  const effectiveStartAdornment = showErrorIcon && isInvalid ? <ErrorIcon color="error" /> : startAdornment;
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        enableAccessibleFieldDOMStructure={false}
        label={label}
        value={date ? dayjs(date) : null}
        onChange={(value) => {
          if (value) {
            onChange?.(value.toDate());
          }
        }}
        disabled={disabled}
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        format={getPattern(DateFormatType.DATE)}
        shouldDisableDate={(date) => (isDayDisabled ? isDayDisabled(date.toDate()) : false)}
        slots={{
          textField: TextField,
          openPickerIcon: CalendarTodayOutlinedIcon,
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8], // 8px vertical gap
                },
              },
            ],
            sx: {
              "& .MuiYearCalendar-button": {
                textAlign: "center",
              },
              // We target the button inside the popper using the stable MUI class
              "& .MuiYearCalendar-button.Mui-selected": {
                backgroundColor: `${C_PRIMARY}!important`,
              },
              "& .MuiYearCalendar-button.Mui-selected:hover": {
                backgroundColor: `${C_PRIMARY}!important`,
              },
              "& .MuiYearCalendar-button.Mui-disabled": {
                color: `${C_ON_SURFACE_LOW_EMPHASIS}!important`,
              },
            },
          },
          day: {
            sx: {
              "&.Mui-selected": {
                backgroundColor: `${C_PRIMARY}!important`,
              },
              "&.Mui-selected:hover": {
                backgroundColor: `${C_PRIMARY}!important`,
              },
              "&.Mui-disabled": {
                color: `${C_ON_SURFACE_LOW_EMPHASIS}!important`,
                backgroundColor: "transparent",
              },
            },
          },
          desktopPaper: {
            sx: {
              borderRadius: "16px",
              background: `${C_SURFACE}`,
              boxShadow: `${ELEVATION_2}`,
            },
          },
          field: {
            clearable: showClear,
            onClear: () => onClear?.(),
          },
          textField: {
            fullWidth: true,
            variant: "outlined",
            error: isInvalid,
            size,
            helperText: helperLabel,
            highlight: isHighlight,
            inputProps: {
              "data-testid": dataTestId,
              placeholder: getPattern(DateFormatType.DATE),
            },
            onFocus,
            onBlur: () => {
              onBlur?.(date ?? undefined);
            },
            InputProps: {
              startAdornment: effectiveStartAdornment ? (
                <InputAdornment position="start">{effectiveStartAdornment}</InputAdornment>
              ) : undefined,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
